import * as React from 'react'

import { PackageIcon, Trash2Icon } from 'lucide-react'
import { makeObservable, observable, runInAction } from 'mobx'
import { Observer, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { AutoSizer, List } from 'react-virtualized'

import { LocationType, PermitFieldType } from '~/client/graph/types.generated'
import { Button } from '~/client/src/shared/components/Button/Button'
import { LocationReference } from '~/client/src/shared/components/ObjectReference/LocationReference'
import { ObjectReference } from '~/client/src/shared/components/ObjectReference/ObjectReference'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import { equipmentPermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'
import { getDeletedObjectLabel } from '~/client/src/shared/utils/getDeletedObjectLabel'

import FieldSectionHeader from '../../../../../../FieldSectionHeader/FieldSectionHeader'
import { IExpandedFilterOption } from '../DynamicFiltersList.store'
import ValuesSelector from './ValuesSelector'

interface IProps {
  isEditMode: boolean
  restrictedLocationIds: string[]
  restrictedLocationTypes: LocationType[]
  isMaterialResourceField?: boolean
  configuredFieldType?: PermitFieldType
  filterOptions: IExpandedFilterOption[]

  onValuesChange: (selectedValues: string[]) => void
  onRemoveOption?: (locationIds: Set<string>) => void
}

const OPTION_ROW_HEIGHT = 50
const OPTIONS_MAX_HEIGHT = 200
const OVERSCAN_ROW_COUNT = 10

@observer
export default class FilterOptionsList extends React.Component<IProps> {
  @observable public selectedOptions: Set<string> = new Set()

  public constructor(props: IProps) {
    super(props)

    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (
      prevProps.filterOptions.length !== this.props.filterOptions.length ||
      (prevProps.isEditMode !== this.props.isEditMode && !this.props.isEditMode)
    ) {
      runInAction(() => {
        this.selectedOptions.clear()
      })
    }
  }

  public render() {
    const {
      filterOptions,
      isEditMode,
      restrictedLocationIds,
      restrictedLocationTypes,
      configuredFieldType,
      onValuesChange,
    } = this.props

    const selectedCount = this.selectedOptions.size
    const totalCount = filterOptions.length
    const isAllSelected = selectedCount === totalCount && totalCount > 0

    return (
      <div className="col mx20 my12">
        <div className="col y-center mb12">
          <FieldSectionHeader
            text={Localization.translator.options}
            isIconHidden
          />
          <span className="text large grey-30 mt10">
            {selectedCount > 0
              ? `${selectedCount}/${totalCount} ${Localization.translator.selected}`
              : `${totalCount} ${Localization.translator.total}`}
          </span>
        </div>

        {!!filterOptions.length && (
          <div className="col ml10">
            <div className="row x-center h40 py8 bg-slate-97 bb-light-input-border">
              <span className="pl8 pr20 no-grow tw:flex tw:justify-center">
                <Checkbox
                  indeterminate={
                    !isAllSelected &&
                    filterOptions.some(option =>
                      this.selectedOptions.has(option.location.id),
                    )
                  }
                  checked={isAllSelected}
                  disabled={!isEditMode}
                  onCheckedChange={() => this.handleSelectAll()}
                />
              </span>
              <div className="text large medium-bold line-extra-large flex-grow">
                {Localization.translator.name}
              </div>
            </div>

            <AutoSizer disableHeight={true}>
              {({ width }) => (
                <List
                  data={filterOptions}
                  width={width}
                  height={Math.min(
                    OPTIONS_MAX_HEIGHT,
                    OPTION_ROW_HEIGHT * filterOptions.length,
                  )}
                  rowHeight={OPTION_ROW_HEIGHT}
                  rowCount={filterOptions.length}
                  rowRenderer={this.renderSelectOption}
                  scrollToAlignment="start"
                  overscanRowCount={OVERSCAN_ROW_COUNT}
                />
              )}
            </AutoSizer>
          </div>
        )}
        {isEditMode && (
          <div className="row y-center">
            <ValuesSelector
              selectedIds={filterOptions.map(l => l.location.id)}
              isEditMode={isEditMode}
              restrictedLocationIds={restrictedLocationIds}
              restrictedLocationTypes={restrictedLocationTypes}
              configuredFieldType={configuredFieldType}
              onChange={onValuesChange}
            />
            {selectedCount > 0 && (
              <div
                className="pointer text large red pa10 ml15 no-flex"
                onClick={this.handleRemoveSelected}
              >
                - {Localization.translator.removeSelected}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  private renderOptionReference(
    { location, hierarchy }: IExpandedFilterOption,
    isMaterialResourceField: boolean,
    isEquipmentField: boolean,
  ): JSX.Element {
    const label = location.isDeleted
      ? getDeletedObjectLabel(location.name)
      : location.name
    const name = <span title={label}>{label}</span>
    const iconName =
      location.iconName ||
      (isEquipmentField ? MapViewLocationIcon.Equipment : null)

    if (isMaterialResourceField || !iconName) {
      return (
        <span className="tw:flex tw:min-w-0 tw:flex-col">
          <ObjectReference
            icon={
              isMaterialResourceField ? (
                <PackageIcon />
              ) : (
                renderResourceSemanticIcon('location')
              )
            }
            variant="plain"
            size="dense"
          >
            {name}
          </ObjectReference>
          {location.stxResourceId && (
            <span className="tw:truncate tw:pl-6 tw:text-app-micro tw:text-muted-foreground">
              {location.stxResourceId}
            </span>
          )}
        </span>
      )
    }

    return (
      <LocationReference
        iconName={iconName as MapViewLocationIcon}
        color={location.color}
        variant="plain"
        size="dense"
        resourceCode={location.stxResourceId}
        parentNames={hierarchy.slice().reverse()}
      >
        {name}
      </LocationReference>
    )
  }

  private renderSelectOption = ({ key, style, index }: any): JSX.Element => {
    const props = { key, style }
    const option = this.props.filterOptions[index]
    const {
      onRemoveOption,
      isEditMode,
      isMaterialResourceField,
      configuredFieldType,
    } = this.props
    const isEquipmentField =
      equipmentPermitFieldTypes.includes(configuredFieldType)

    return (
      <div
        {...props}
        className={classList({
          'row x-center highlighted-hover hover-visible-container': true,
          'bt-light-input-border': !!index,
        })}
      >
        <Observer>
          {() => (
            <>
              <span className="pl8 pr20 no-grow tw:flex tw:justify-center">
                <Checkbox
                  checked={this.selectedOptions.has(option.location.id)}
                  disabled={!isEditMode}
                  onCheckedChange={() =>
                    this.handleOptionChange(option.location.id)
                  }
                />
              </span>
              <div className="col overflow-hidden pr10 flex-grow">
                <div className="row y-center">
                  {this.renderOptionReference(
                    option,
                    isMaterialResourceField,
                    isEquipmentField,
                  )}
                </div>
              </div>
              {isEditMode && (
                <Button
                  variant="destructive-ghost"
                  size="icon-sm"
                  className="hover-visible tw:flex-none! tw:mr-2.5"
                  aria-label={Localization.translator.delete}
                  onClick={e => {
                    e.stopPropagation()
                    onRemoveOption(new Set([option.location.id]))
                  }}
                >
                  <Trash2Icon />
                </Button>
              )}
            </>
          )}
        </Observer>
      </div>
    )
  }

  private handleSelectAll = () => {
    const { filterOptions } = this.props
    const selectedCount = this.selectedOptions.size
    const totalCount = filterOptions.length
    const isAllSelected = selectedCount === totalCount && totalCount > 0

    runInAction(() => {
      if (isAllSelected) {
        this.selectedOptions.clear()
      } else {
        filterOptions.forEach(option => {
          this.selectedOptions.add(option.location.id)
        })
      }
    })
  }

  private handleRemoveSelected = () => {
    const { onRemoveOption } = this.props

    if (onRemoveOption && this.selectedOptions.size > 0) {
      onRemoveOption(this.selectedOptions)
    }

    runInAction(() => {
      this.selectedOptions.clear()
    })
  }

  private handleOptionChange = (optionId: string) => {
    runInAction(() => {
      if (this.selectedOptions.has(optionId)) {
        this.selectedOptions.delete(optionId)
      } else {
        this.selectedOptions.add(optionId)
      }
    })
  }
}
